import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Bot, ClipboardList, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useContactInfo, getZaloHref, getWhatsAppHref } from '../contexts/ContactInfoContext';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Official VietClinics WhatsApp — used as a guaranteed fallback for the chat
// chooser's "Chat on WhatsApp" option when contactInfo has no number loaded.
const WHATSAPP_FALLBACK_URL = 'https://wa.me/84386492508';
const ENQUIRY_SENTINEL = '[OPEN_ENQUIRY_FORM]';
const FOLLOWUPS_SENTINEL_RE = /\[FOLLOWUPS\]\s*([^\n]+)/i;

/** Parse `[FOLLOWUPS] a || b || c` line. Returns { cleaned, followUps }. */
const extractFollowUps = (raw) => {
  if (!raw) return { cleaned: '', followUps: [] };
  const m = raw.match(FOLLOWUPS_SENTINEL_RE);
  if (!m) return { cleaned: raw, followUps: [] };
  const followUps = m[1]
    .split('||')
    .map((s) => s.trim().replace(/^[•\-·*]+\s*/, '').replace(/[?.]+$/, ''))
    .filter((s) => s.length > 0 && s.length <= 40)
    .slice(0, 4);
  const cleaned = raw.replace(FOLLOWUPS_SENTINEL_RE, '').replace(/\n{3,}/g, '\n\n').trim();
  return { cleaned, followUps };
};

const EMPTY_FORM = {
  full_name: '',
  country: '',
  whatsapp_number: '',
  email: '',
  treatment_interested: '',
  preferred_city: '',
  preferred_date: '',
  notes: '',
};

/**
 * Quick suggestion chips shown under the welcome message.
 * - `prompt` (string)     → auto-sends to the AI on tap
 * - `openForm: true`      → opens the enquiry form directly
 * - `href` (string)       → navigates to a route (new tab)
 * - `fallback` (string)   → patient-friendly reply shown if the AI call fails.
 *                           Should educate at a high level + nudge to
 *                           "Request Appointment" / coordinator. Never a
 *                           technical error.
 * Add / reorder by editing this array — no other code changes needed.
 */
const SUGGESTIONS = [
  {
    label: 'Dental Implants',
    prompt: 'Tell me about dental implants in Vietnam — what to expect and typical pricing.',
    fbKey: 'dental',
    fallback:
      'Dental implants in Vietnam are typically performed by internationally-trained specialists at JCI-accredited clinics, using world-class implant brands (Straumann, Nobel Biocare). Typical pricing is around US$450–$1,500 per implant — usually 50–70% less than the US/UK/Australia. Most patients stay 7–10 days for the initial procedure and return later for the crown.\n\nFor a personalised plan and clinic recommendations, tap **Request Appointment** below — a coordinator will reach out within 24 hours.',
  },
  {
    label: 'Cosmetic Surgery',
    prompt: 'What cosmetic surgery options does VietClinics coordinate in Vietnam?',
    fbKey: 'cosmetic',
    fallback:
      'Through our verified partner clinics, VietClinics coordinates rhinoplasty, breast augmentation/reduction, liposuction, eyelid surgery, facial contouring and more — performed by surgeons trained in Korea, the US and Europe. Typical savings are 40–60% vs the US/UK with comparable safety standards.\n\nTap **Request Appointment** below to share your treatment interest and a coordinator will put together a personalised plan for you.',
  },
  {
    label: 'Hair Transplant',
    prompt: 'Tell me about hair transplant (FUE / DHI) in Vietnam — pricing and recovery.',
    fbKey: 'hair',
    fallback:
      'Hair transplants in Vietnam (Sapphire FUE and DHI techniques) typically range from US$1,800–$3,500 for a full session, depending on graft count. The procedure takes 6–8 hours under local anaesthetic, you can fly home in 2–3 days, and initial results show in 4–6 months.\n\nFor a personalised quote and graft estimate, tap **Request Appointment** — our coordinator will arrange a virtual consultation with one of our partner surgeons.',
  },
  {
    label: 'IVF Treatment',
    prompt: 'Tell me about IVF treatment in Vietnam — success rates, pricing and timeline.',
    fbKey: 'ivf',
    fallback:
      'VietClinics works with JCI-accredited fertility centres offering IVF, ICSI and PGT-A. A standard IVF cycle is typically US$3,500–$6,000 — significantly less than Western pricing while maintaining international clinical standards. The full cycle usually takes 4–6 weeks with 2 trips to Vietnam.\n\nFertility journeys are deeply personal — tap **Request Appointment** below and one of our fertility coordinators will reach out privately within 24 hours.',
  },
  {
    label: 'Emergency Support',
    href: '/emergency',
    fbKey: 'emergency',
    fallback:
      '⚠️ **VietClinics is not an emergency medical provider.**\n\nFor life-threatening emergencies, please contact local emergency services immediately.\nIn Vietnam: dial **115** for an ambulance or **113** for police.\n\nFor non-emergency urgent care (hotel doctor visits, IV therapy, wound care), VietClinics can coordinate support with verified local clinic partners — tap **Request Appointment** to get started.',
  },
  {
    label: 'Prices in Vietnam',
    prompt: 'How do treatment prices in Vietnam compare to the US, UK and Australia?',
    fbKey: 'prices',
    fallback:
      'Treatment in Vietnam typically costs 40–70% less than the US, UK or Australia — at internationally-comparable clinical standards. Example ranges:\n• Dental implant: US$450–$1,500 (vs $3,000–$6,000 in the US)\n• Rhinoplasty: US$1,800–$3,500 (vs $7,000–$12,000)\n• IVF cycle: US$3,500–$6,000 (vs $12,000–$20,000)\n• Hair transplant: US$1,800–$3,500 (vs $8,000–$15,000)\n\nFor a personalised quote, tap **Request Appointment** below.',
  },
  {
    label: 'Travel Support',
    prompt: 'What travel support does VietClinics offer — visa, transport, hotel and recovery stays?',
    fbKey: 'travel',
    fallback:
      'VietClinics coordinates the full travel side of your healthcare journey: visa guidance, airport pickup, hotel/recovery-stay recommendations near your clinic, English-speaking translators where needed, and follow-up support after you fly home. Everything in one coordinated request.\n\nTap **Request Appointment** below and tell us your dates — a coordinator will tailor the right support to your trip.',
  },
  {
    label: 'Speak with Coordinator',
    openForm: true,
    // No fallback needed — this never calls the AI; it opens the form directly.
  },
];

/** Fallback follow-up sets for chip-failure paths. */
const FALLBACK_FOLLOWUPS = {
  generic: ['Request appointment', 'Speak with coordinator', 'Browse treatments'],
  dental: ['Recovery time', 'Travel planning', 'Speak with coordinator'],
  cosmetic: ['Typical pricing', 'Recovery time', 'Speak with coordinator'],
  hair: ['Recovery time', 'Trusted clinics', 'Speak with coordinator'],
  ivf: ['Success rates', 'Timeline', 'Speak with coordinator'],
  prices: ['Travel planning', 'Speak with coordinator', 'Request appointment'],
  travel: ['Airport pickup', 'Recovery hotels', 'Visa support'],
  emergency: ['Doctor hotel visit', 'IV therapy', 'Request support'],
};

const FRIENDLY_FALLBACK_DEFAULT =
  "Thanks for your message — I'd be happy to help. For the most accurate answer to your question, one of our VietClinics coordinators can follow up with you. Tap **Request Appointment** below if you'd like us to reach out.";

/**
 * Local FAQ short-circuit layer.
 *
 * Catches a small set of high-frequency, well-known questions and answers
 * them INSTANTLY with a curated reply — without calling the AI. Benefits:
 *   • Zero credit cost
 *   • Never fails (no network/API dependency)
 *   • Always on-message
 *   • Consistent brand voice
 *
 * Each entry: { match: RegExp[], reply: string, followUps: string[] }
 * Tested in order — first hit wins. Keep `match` patterns narrow.
 */
const LOCAL_FAQ = [
  // ── Order matters: more specific patterns must come first ─────────────────
  {
    // English / language support — narrow patterns, safe to evaluate first
    match: [
      /\b(english|language)\b.*\b(speaking|support|spoken|staff|doctor|coordinator|translator)\b/i,
      /\bdo (you|they|the doctors?) speak english\b/i,
      /\benglish at (the )?clinic\b/i,
      /\b(translator|interpreter)\b/i,
    ],
    reply:
      'Yes — English-speaking support is available at most of our partner clinics, and the VietClinics coordinator team is fully English-speaking. We can also arrange translators on request.',
    followUps: ['Request appointment', 'Travel support', 'Browse treatments'],
  },
  {
    // Clinic verification / vetting / accreditation / safety
    match: [
      /\b(verify|verified|vetted|vetting|accredit|accredited|certified|trust|trusted)\b.*\b(clinic|partner|hospital|doctor|surgeon)\b/i,
      /\b(clinic|partner|hospital|doctor|surgeon)s?\b.*\b(verified|vetted|accredited|certified|trusted|safe)\b/i,
      /\bhow\s+(do|are)\s+(you|the\s+)?(vet|verify|check|choose|select|vetting)\s+(your\s+)?(clinics?|partners?)\b/i,
      /\b(are|is)\s+(the|your)\s+clinic.*(safe|good|reputable)/i,
    ],
    reply:
      'Yes — every partner clinic is vetted by our team. We check credentials, accreditation (JCI where applicable), surgeon training and patient outcomes before adding any clinic to the VietClinics network.\n\nYou\'ll only ever be matched with clinics we trust personally.',
    followUps: ['Browse treatments', 'Request appointment', 'Travel support'],
  },
  {
    // Payment to clinic — MUST come before service-fee, otherwise "pay the
    // clinic" gets swallowed by the broader fee pattern.
    match: [
      /\b(how|where|what)\s+(do|to|can|are)\s+(i|we|patients?)?\s*(pay|payment)\s+(method|the )?(clinic|hospital|doctor|surgeon|partner)/i,
      /\b(payment|paying)\s+(the\s+)?(clinic|hospital|doctor|surgeon|partner)\b/i,
      /\b(payment|pay)\s+method/i,
      /\b(card|bank transfer|cash|usd|vnd)\b.*\b(accept|treatment|clinic)/i,
    ],
    reply:
      "You pay the clinic directly — usually in USD or VND, by card or bank transfer, once your consultation and treatment plan are confirmed. VietClinics doesn't handle payments, so there's no middleman markup.",
    followUps: ['Request appointment', 'Speak with coordinator', 'Browse treatments'],
  },
  {
    // Service fee / commission / "do I pay extra?" / free for patients / business model
    match: [
      /\b(pay|charge|fee|cost|commission|markup)\b.*\b(extra|use|using|service|vietclinic|platform|you|your)\b/i,
      /\b(extra|additional|hidden)\b.*\b(pay|fee|charge|cost)\b/i,
      /\bany hidden (fee|charge|cost)/i,
      /\bis there (a |any )?(fee|charge|cost|commission|markup)\b/i,
      /\b(are|is) (your |the |this )?(service|vietclinic|platform|it|this)s?\b.*\b(free|paid|charge|fee)\b/i,
      /how (do|does) (you|vietclinic).*\b(make money|earn|paid|monetize)\b/i,
      /\bdo (i|we|patients?) pay (you|vietclinic|anything|extra|any\s+(commission|fee|charge))\b/i,
      /\b(pay|paying)\b.*\b(commission|markup|fee|charge)\b/i,
    ],
    reply:
      'No, patients do not pay extra to use VietClinics. Our coordination support is **free for patients** — you pay the clinic directly for your treatment.\n\nVietClinics helps you:\n• Understand your treatment options\n• Compare verified clinic partners\n• Request appointments\n• Get coordinator support before, during and after your trip',
    followUps: ['Request appointment', 'Browse treatments', 'How are clinics verified?'],
  },
];

/**
 * Try to answer the user's message locally before calling the AI.
 * Returns a matched FAQ entry or null.
 */
const matchLocalFAQ = (text) => {
  if (!text) return null;
  for (const entry of LOCAL_FAQ) {
    if (entry.match.some((re) => re.test(text))) return entry;
  }
  return null;
};

const FloatingChat = ({ embedded = false }) => {
  const { pathname } = useLocation();
  const { contactInfo } = useContactInfo();
  const [isOpen, setIsOpen] = useState(false);
  const [activeChat, setActiveChat] = useState(null); // 'ai', 'whatsapp', 'zalo'
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text:
        "Hello, and welcome to VietClinics 🇻🇳\n" +
        "I'm your AI concierge — here to help you explore treatments, pricing, recovery, travel support, and everything in between.\n\n" +
        'How can I help you today?',
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const sessionIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Once the user closes the form, suppress AI auto-opens for the rest of
  // this session. Only an explicit user click (pill, chip, follow-up) can
  // reopen the form after that — see `forceOpenForm()`.
  const formManuallyClosedRef = useRef(false);

  // Auto-scroll to newest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, sending]);

  const whatsappHref = getWhatsAppHref(contactInfo, 'Hi VietClinics, I need help finding a clinic.') || WHATSAPP_FALLBACK_URL;
  const zaloHref = getZaloHref(contactInfo);

  const handleWhatsApp = () => {
    window.open(whatsappHref || WHATSAPP_FALLBACK_URL, '_blank', 'noopener,noreferrer');
  };

  const handleZalo = () => {
    if (!zaloHref) return;
    window.open(zaloHref, '_blank');
  };

  const handleSendMessage = async (e, prefillText, fallbackOverride, fbKey) => {
    if (e && e.preventDefault) e.preventDefault();
    const text = (prefillText ?? inputValue).trim();
    if (!text || sending) return;

    // ── Local FAQ short-circuit: answer instantly without calling the AI.
    const faq = matchLocalFAQ(text);
    if (faq) {
      setMessages((prev) => [
        ...prev,
        { type: 'user', text },
        { type: 'bot', text: faq.reply, followUps: faq.followUps },
      ]);
      setInputValue('');
      return;
    }

    // Build history payload from the existing conversation BEFORE adding the new turn.
    const history = messages.map((m) => ({
      role: m.type === 'user' ? 'user' : 'assistant',
      text: m.text,
    }));

    setMessages((prev) => [...prev, { type: 'user', text }]);
    setInputValue('');
    setSending(true);

    try {
      const res = await axios.post(
        `${API_BASE}/chat`,
        {
          message: text,
          session_id: sessionIdRef.current,
          history,
        },
        { timeout: 30000 },
      );
      const reply = res?.data?.reply?.trim();
      if (res?.data?.session_id) sessionIdRef.current = res.data.session_id;
      if (reply) {
        // Detect (and strip) the booking-intent sentinel emitted by the AI.
        const hasSentinel = reply.includes(ENQUIRY_SENTINEL);
        const afterEnquiry = hasSentinel
          ? reply.replace(ENQUIRY_SENTINEL, '').replace(/\n{3,}/g, '\n\n').trim()
          : reply;
        // Extract contextual follow-up chips, if any.
        const { cleaned, followUps } = extractFollowUps(afterEnquiry);
        if (cleaned) {
          setMessages((prev) => [...prev, { type: 'bot', text: cleaned, followUps }]);
        }
        // Auto-open ONLY when: (a) AI emitted the sentinel AND
        //                      (b) user hasn't manually closed the form yet
        // Even when suppressed, the bot reply still shows "Request appointment"
        // as a follow-up chip so the user can self-trigger.
        if (hasSentinel && !formManuallyClosedRef.current) {
          setShowForm(true);
        }
      } else {
        throw new Error('Empty reply');
      }
    } catch (err) {
      // NEVER show a technical error to a patient. Use the chip's curated
      // fallback if this message came from a chip click, otherwise use the
      // friendly default that nudges to the enquiry form.
      const fallback = fallbackOverride || FRIENDLY_FALLBACK_DEFAULT;
      const followUps = FALLBACK_FOLLOWUPS[fbKey] || FALLBACK_FOLLOWUPS.generic;
      setMessages((prev) => [...prev, { type: 'bot', text: fallback, followUps }]);
    } finally {
      setSending(false);
    }
  };

  const handleFormChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSuggestionClick = (s) => {
    if (s.openForm) {
      forceOpenForm();
      return;
    }
    if (s.href) {
      // Surface the calm safety message in the chat too, so even if the user
      // doesn't follow the link they still see the emergency guidance.
      if (s.fallback) {
        setMessages((prev) => [
          ...prev,
          { type: 'user', text: s.label },
          { type: 'bot', text: s.fallback, followUps: FALLBACK_FOLLOWUPS[s.fbKey] || FALLBACK_FOLLOWUPS.generic },
        ]);
      }
      window.open(s.href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (s.prompt) {
      handleSendMessage(null, s.prompt, s.fallback, s.fbKey);
    }
  };

  /**
   * Tap on a contextual follow-up chip rendered under a previous bot message.
   * Sends the chip text as if the user typed it.
   */
  const handleFollowUpClick = (text) => {
    // Special-case "Speak with coordinator" / "Request appointment" → open form
    const t = text.toLowerCase();
    if (
      t.includes('coordinator') ||
      t.includes('request appointment') ||
      t.includes('request support') ||
      t.includes('book') ||
      t.includes('book now') ||
      t.includes('submit treatment') ||
      t.includes('book consultation')
    ) {
      forceOpenForm();
      return;
    }
    handleSendMessage(null, text, null, null);
  };

  // Show suggestion chips only before the user has interacted (clean UX).
  const hasUserSpoken = messages.some((m) => m.type === 'user');
  const showSuggestions = !hasUserSpoken && !sending && !showForm;

  /**
   * Open the form because the user *explicitly* asked for it (pill, follow-up
   * chip, suggestion chip). Clears the "manually closed" suppression so the
   * AI can later auto-open again only AFTER the user has actively re-engaged.
   */
  const forceOpenForm = () => {
    formManuallyClosedRef.current = false;
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormError(null);
    // Remember the user dismissed the form so the AI doesn't auto-reopen it.
    formManuallyClosedRef.current = true;
  };

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    if (submittingForm) return;

    // Quick required-field check (mirror backend min_length=2 / 5)
    const required = ['full_name', 'country', 'whatsapp_number', 'email', 'treatment_interested', 'preferred_city'];
    for (const k of required) {
      if (!form[k] || !form[k].trim()) {
        setFormError('Please fill in all required fields.');
        return;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      setFormError('Please enter a valid email address.');
      return;
    }

    setSubmittingForm(true);
    setFormError(null);

    try {
      const res = await axios.post(
        `${API_BASE}/chat/enquiry`,
        { ...form, session_id: sessionIdRef.current },
        { timeout: 20000 },
      );
      const ref = res?.data?.ref || '';
      setShowForm(false);
      setForm(EMPTY_FORM);
      setMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          text:
            `Thank you, ${form.full_name.split(' ')[0]} — your enquiry has been sent to our coordinator team ✅\n\n` +
            (ref ? `Reference: ${ref}\n` : '') +
            "We'll reach out on WhatsApp or email within 24 hours with personalised options.\n\n" +
            'In the meantime, feel free to ask me anything else about treatments, travel support, or recovery.',
        },
      ]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send your enquiry. Please try again or message us on WhatsApp.";
      setFormError(msg);
    } finally {
      setSubmittingForm(false);
    }
  };

  // Hide the floating launcher on auth pages where it would overlap the
  // Sign In button / password field on mobile.
  if (/\/(login|clinic-login)\/?$/.test(pathname)) return null;
  // Contact owns an in-flow instance so neither its launcher nor expanded
  // panel can cover the form, contact details or Quick Help.
  if (!embedded && /\/contact\/?$/.test(pathname)) return null;

  return (
    <>
      {/* Chat Buttons */}
      <div
        className={embedded ? 'relative flex flex-col items-start gap-3 w-full sm:max-w-sm' : 'fixed bottom-24 right-4 sm:bottom-20 sm:right-6 z-[9998] flex flex-col items-end gap-3'}
        data-testid="chat-widget"
      >
        {/* Expanded Options */}
        {isOpen && !activeChat && (
          <div data-testid="chat-channel-options" className={`bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 mb-2 animate-in slide-in-from-bottom-4 duration-300 ${embedded ? 'w-full' : 'w-72'}`}>
            <p className="text-sm font-semibold text-gray-900 mb-3">How would you like to chat?</p>
            
            {/* AI Chat */}
            <button
              onClick={() => setActiveChat('ai')}
              data-testid="chat-ai-option"
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#1FA7A1] to-[#1E88E5] text-white hover:opacity-90 transition-opacity mb-2"
            >
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="font-semibold">VietClinics AI Assistant</p>
                <p className="text-xs text-white/80">Online • Instant Guidance</p>
              </div>
            </button>

            {/* Chat on WhatsApp — always shown alongside the AI assistant */}
            <button
              onClick={handleWhatsApp}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors mb-2"
              data-testid="floating-chat-whatsapp-btn"
            >
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="font-semibold">Chat on WhatsApp</p>
                <p className="text-xs text-white/80">Chat with support</p>
              </div>
            </button>

            {/* Zalo */}
            {zaloHref && (
              <button
                onClick={handleZalo}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#0068FF] text-white hover:bg-[#0057D4] transition-colors"
                data-testid="floating-chat-zalo-btn"
              >
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-lg">
                  Z
                </div>
                <div className="text-left">
                  <p className="font-semibold">Zalo</p>
                  <p className="text-xs text-white/80">Popular in Vietnam</p>
                </div>
              </button>
            )}
          </div>
        )}

        {/* AI Chat Window */}
        {activeChat === 'ai' && (
          <div data-testid="chat-ai-panel" className={`bg-white rounded-2xl shadow-2xl border border-gray-100 ${embedded ? 'w-full' : 'w-80 sm:w-96'} animate-in slide-in-from-bottom-4 duration-300 overflow-hidden`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1FA7A1] to-[#1E88E5] p-4 flex items-center justify-between">
              <div className={`flex items-center gap-3 ${embedded ? 'min-w-0' : ''}`}>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-white flex items-center gap-2">
                    VietClinics AI Assistant
                    <span className="relative inline-flex h-2 w-2" aria-label="online">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
                    </span>
                  </p>
                  <p className="text-xs text-white/80">Online • Typically replies instantly</p>
                </div>
              </div>
              <button 
                onClick={() => setActiveChat(null)}
                data-testid="chat-ai-close"
                aria-label="Close AI chat"
                className="text-white/80 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="h-72 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
              {messages.map((msg, idx) => (
                <div key={idx} className="space-y-2">
                  <div className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl ${
                      msg.type === 'user'
                        ? 'bg-[#1FA7A1] text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-800 rounded-bl-md'
                    }`}>
                      <p className="text-[13.5px] whitespace-pre-line leading-[1.55]">{msg.text}</p>
                    </div>
                  </div>
                  {msg.type === 'bot' && Array.isArray(msg.followUps) && msg.followUps.length > 0 && (
                    <div
                      className="flex flex-wrap gap-1.5 pl-1 animate-fade-in"
                      data-testid={`ai-chat-followups-${idx}`}
                    >
                      {msg.followUps.map((q, i) => (
                        <button
                          key={`${idx}-${i}-${q}`}
                          type="button"
                          onClick={() => handleFollowUpClick(q)}
                          className="text-[11.5px] font-semibold text-[#1FA7A1] bg-white border border-[#1FA7A1]/35 rounded-full px-2.5 py-1 hover:bg-[#1FA7A1] hover:text-white hover:border-[#1FA7A1] transition-colors"
                          data-testid={`ai-chat-followup-${idx}-${i}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Quick suggestion chips — auto-hidden once the user starts chatting */}
              {showSuggestions && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="ai-chat-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      onClick={() => handleSuggestionClick(s)}
                      className="text-[12px] font-semibold text-[#1FA7A1] bg-white border border-[#1FA7A1]/35 rounded-full px-3 py-1.5 hover:bg-[#1FA7A1] hover:text-white hover:border-[#1FA7A1] transition-colors"
                      data-testid={`ai-suggestion-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
              {sending && (
                <div className="flex justify-start" data-testid="ai-chat-typing">
                  <div className="bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md p-3">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Request Appointment pill (above input) */}
            {!showForm && (
              <div className="px-4 pt-2 pb-1">
                <button
                  type="button"
                  onClick={forceOpenForm}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-[#1FA7A1] bg-[#1FA7A1]/10 hover:bg-[#1FA7A1]/15 transition-colors"
                  data-testid="ai-chat-open-enquiry"
                >
                  <ClipboardList className="h-4 w-4" />
                  Request Appointment
                </button>
              </div>
            )}

            {/* Inline Enquiry Form (drawer-style, replaces the input bar) */}
            {showForm ? (
              <EnquiryForm
                form={form}
                onChange={handleFormChange}
                onSubmit={handleSubmitEnquiry}
                onClose={closeForm}
                submitting={submittingForm}
                error={formError}
              />
            ) : (
              /* Input */
              <form onSubmit={handleSendMessage} className="p-4 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Ask about treatments, clinics, prices, recovery, or travel support"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={sending}
                  className={`flex-1 ${embedded ? 'min-w-0' : ''} px-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/50 disabled:bg-slate-50`}
                  data-testid="ai-chat-input"
                />
                <button
                  type="submit"
                  disabled={sending || !inputValue.trim()}
                  className="w-10 h-10 bg-[#1FA7A1] text-white rounded-full flex items-center justify-center hover:bg-[#178f8a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="ai-chat-send"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p
                className="text-[10px] text-slate-400 text-center mt-2 leading-snug px-1"
                data-testid="ai-chat-disclaimer"
              >
                VietClinics coordinates healthcare support with verified local clinic partners across Vietnam.
                For life-threatening emergencies, please contact local emergency services immediately.
              </p>
            </form>
            )}
          </div>
        )}

        {/* Subtle "Urgent care" badge — hidden when the chat is open.
            Wording is intentionally generic: invites a question, makes no
            availability, pricing or response-time promises. */}
        {!isOpen && !activeChat && (
          <button
            onClick={() => setIsOpen(true)}
            className={`${embedded ? 'self-start' : 'self-end'} mb-[-6px] mr-[2px] inline-flex items-center gap-1.5 bg-white text-slate-800 font-bold text-[11px] tracking-wide px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1FA7A1] hover:text-[#1FA7A1] transition-colors`}
            style={{ boxShadow: '0 6px 18px rgba(15,42,85,0.12)' }}
            aria-label={embedded ? 'Ask VietClinics' : 'Ask about urgent care'}
            data-testid="chat-urgent-care-badge"
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping" style={{ background: '#1FA7A1' }} />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: '#1FA7A1' }} />
            </span>
            {embedded ? 'Ask VietClinics' : 'Ask about urgent care'}
          </button>
        )}

        {/* Main Toggle Button */}
        <button
          onClick={() => {
            if (activeChat) {
              setActiveChat(null);
            } else {
              setIsOpen(!isOpen);
            }
          }}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
            isOpen || activeChat
              ? 'bg-gray-700 text-white rotate-0'
              : 'bg-gradient-to-r from-[#1FA7A1] to-[#1E88E5] text-white'
          }`}
          data-testid="chat-toggle-btn"
          aria-label={isOpen || activeChat ? 'Close chat options' : 'Open chat options'}
          aria-expanded={Boolean(isOpen || activeChat)}
        >
          {isOpen || activeChat ? (
            <X className="h-6 w-6" />
          ) : (
            <MessageCircle className="h-6 w-6" />
          )}
        </button>
      </div>
    </>
  );
};

export default FloatingChat;

/* ────────────────────────────────────────────────────────────────────────── */
/* Inline 8-field enquiry form rendered inside the AI chat window.            */
/* ────────────────────────────────────────────────────────────────────────── */

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
      {label}
      {required && <span className="text-[#E84A3C] ml-0.5">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/50 focus:border-[#1FA7A1] transition-colors';

const EnquiryForm = ({ form, onChange, onSubmit, onClose, submitting, error }) => (
  <form
    onSubmit={onSubmit}
    className="border-t bg-white max-h-[60vh] overflow-y-auto"
    data-testid="ai-chat-enquiry-form"
  >
    <div className="flex items-center justify-between px-4 pt-3">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-[#1FA7A1]" />
        <span className="text-sm font-bold text-slate-900">Request an Appointment</span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="text-slate-400 hover:text-slate-700"
        aria-label="Close form"
        data-testid="ai-chat-enquiry-close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
    <p className="px-4 pt-1 pb-3 text-xs text-slate-500">
      A VietClinics coordinator will reach out within 24 hours.
    </p>

    <div className="px-4 pb-3 space-y-3">
      <Field label="Full name" required>
        <input
          type="text"
          value={form.full_name}
          onChange={onChange('full_name')}
          className={inputCls}
          placeholder="Jane Smith"
          data-testid="enquiry-full-name"
          required
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Country" required>
          <input
            type="text"
            value={form.country}
            onChange={onChange('country')}
            className={inputCls}
            placeholder="Australia"
            data-testid="enquiry-country"
            required
          />
        </Field>
        <Field label="Preferred city" required>
          <input
            type="text"
            value={form.preferred_city}
            onChange={onChange('preferred_city')}
            className={inputCls}
            placeholder="Ho Chi Minh City"
            data-testid="enquiry-city"
            required
          />
        </Field>
      </div>
      <Field label="WhatsApp number" required>
        <input
          type="tel"
          value={form.whatsapp_number}
          onChange={onChange('whatsapp_number')}
          className={inputCls}
          placeholder="+61 412 345 678"
          data-testid="enquiry-whatsapp"
          required
        />
      </Field>
      <Field label="Email" required>
        <input
          type="email"
          value={form.email}
          onChange={onChange('email')}
          className={inputCls}
          placeholder="you@example.com"
          data-testid="enquiry-email"
          required
        />
      </Field>
      <Field label="Treatment interested in" required>
        <input
          type="text"
          value={form.treatment_interested}
          onChange={onChange('treatment_interested')}
          className={inputCls}
          placeholder="e.g. Dental implants, IVF, Hair transplant"
          data-testid="enquiry-treatment"
          required
        />
      </Field>
      <Field label="Preferred appointment date">
        <input
          type="text"
          value={form.preferred_date}
          onChange={onChange('preferred_date')}
          className={inputCls}
          placeholder="e.g. Sept 2025, flexible"
          data-testid="enquiry-date"
        />
      </Field>
      <Field label="Additional notes">
        <textarea
          value={form.notes}
          onChange={onChange('notes')}
          className={`${inputCls} resize-none`}
          rows={3}
          placeholder="Anything else our coordinator should know?"
          data-testid="enquiry-notes"
        />
      </Field>

      {error && (
        <div
          className="text-[12px] text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-lg px-3 py-2"
          data-testid="enquiry-error"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 text-white font-bold rounded-xl px-4 py-3 text-sm transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
        style={{
          background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
          boxShadow: '0 10px 22px rgba(31,167,161,0.22)',
        }}
        data-testid="enquiry-submit"
      >
        {submitting ? 'Sending…' : 'Send Enquiry'}
      </button>

      <p className="text-[10.5px] text-slate-400 text-center pt-1 leading-snug">
        By submitting, you agree to be contacted by a VietClinics coordinator about your enquiry.
      </p>
    </div>
  </form>
);
